import { useEffect, useRef } from "react";
import {
  useLocalRuntime,
  type AssistantRuntime,
  type ChatModelAdapter,
  type ChatModelRunOptions,
  type ChatModelRunResult,
  type ThreadMessageLike,
  type SuggestionAdapter,
} from "@assistant-ui/react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  clearActivity,
  pushActivity,
  setActiveThreadId,
  setStatusText,
  upsertThread,
  // renameThread,
} from "../store/slices/copilotSlice";
import {
  clearUploadSettled,
  waitForUploadSettled,
} from "../lib/analysisBridge";
import {
  createCopilotMessage,
  createCopilotThread,
  // generateCopilotThreadTitle,
} from "../lib/copilotApi";

// ─── SSE event shapes from the triage-stream endpoint ────────────────────────

interface SSEStatusEvent {
  type: "status";
  status: string;
}

interface SSEReasoningEvent {
  type: "reasoning";
  content: string;
}

interface SSETextEvent {
  type: "text";
  content: string;
}

interface SSESuspensionEvent {
  type: "suspension";
  status: "SUSPENDED_AWAITING_MCAP_APPROVAL";
  payload: MCAPSuspensionPayload;
}

interface SSEUsageEvent {
  type: "usage";
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  credits_consumed: number;
  credit_balance: number;
}

type SSEEvent =
  | SSEStatusEvent
  | SSEReasoningEvent
  | SSETextEvent
  | SSESuspensionEvent
  | SSEUsageEvent;

export interface CopilotCreditUsage {
  creditsConsumed: number;
  creditBalance: number;
}

export interface MCAPSuspensionPayload {
  justification: string;
  estimated_file_size_bytes: number;
  device_id: string;
  device_name?: string;
  incident_id: string;
  incident_reference?: string;
  session_id?: string;
  thread_id?: string;
  /** Rolling-buffer pipelines to upload (MCAP uploads are pipeline-granular). */
  pipelines?: string[];
  time_window_seconds?: number;
  /**
   * Set during thread re-hydration when deep-analysis findings were already
   * appended after this suspension — proof the extraction+analysis completed,
   * regardless of the (possibly overwritten) workflow node or localStorage.
   */
  analysis_present?: boolean;
}

export function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    CONNECTING: "Connecting to copilot service",
    ROUTING_INTENT: "Classifying request",
    GENERATING_RESPONSE: "Generating response",
    HYDRATING_TELEMETRY: "Hydrating baseline telemetry",
    RUNNING_LLM_EVALUATION: "Running analytical evaluation",
    INSPECTING_ROLLING_BUFFER: "Inspecting rolling-buffer configuration",
    CHECKING_EXISTING_MCAP: "Checking for an existing recording",
    DECISION_REUSE_EXISTING_MCAP:
      "Decision reached: reusing an existing recording",
    REUSING_EXISTING_MCAP: "Reusing an already-uploaded recording",
    QUERYING_INCIDENT_DATA: "Re-querying the incident's data",
    COMPOSING_FINDINGS_REPORT: "Composing the findings report",
    DECISION_CONCLUSIVE: "Decision reached: conclusive root cause",
    DECISION_INCONCLUSIVE_REQUEST_MCAP:
      "Decision reached: MCAP extraction required",
    DECISION_DATA_UNAVAILABLE:
      "Decision reached: insufficient data for extraction",
    SUSPENDED_AWAITING_MCAP_APPROVAL: "Awaiting MCAP approval",
    WAITING_FOR_ARTIFACTS: "Waiting for uploaded MCAP artifacts",
    ANALYZING_MCAP: "Cross-examining MCAP recording",
    ANALYSIS_READY: "MCAP ready for deep analysis",
    ANALYSIS_COMPLETE: "Deep analysis complete",
    ANALYSIS_FAILED: "Deep analysis failed",
    IDLE: "Analysis completed",
    ERROR: "Pipeline error",
  };
  return labels[status] ?? status.replace(/_/g, " ");
}

export function copilotHttpError(
  status: number,
  statusText: string,
  payload?: unknown,
): { text: string; error: string } {
  const errorCode =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>).code
      : undefined;
  if (status === 402 || errorCode === "INSUFFICIENT_AI_CREDITS") {
    const error = "Your organization has used all included AI credits.";
    return {
      text: `${error}\n\nTo continue using Copilot, [enable AI credit overages in Billing & Plans](/settings/billing).`,
      error,
    };
  }
  return {
    text: `Backend error: ${status} ${statusText}`,
    error: `HTTP ${status}: ${statusText}`,
  };
}

function splitReasoningChunks(content: string): string[] {
  return content
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function reasoningKind(line: string): "tool" | "decision" {
  if (line.includes("Verdict") || line.includes("Decision")) {
    return "decision";
  }
  return "tool";
}

// ─── Request shape ────────────────────────────────────────────────────────────

interface ChatStreamRequest {
  session_id: string;
  prompt?: string;
  incident_id?: string;
  device_id?: string;
  thread_id?: string;
  ui_route?: string;
}

/**
 * Reads the attachments pinned to the most recent user message (context
 * chips added via the composer's "+" menu, or attached programmatically by
 * an external trigger). These travel with that specific message and are
 * persisted verbatim to the `copilot_messages.attachments` column.
 */
function extractLastUserAttachments(
  messages: ChatModelRunOptions["messages"],
): unknown[] {
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  if (!lastUser) return [];
  return [
    ...((lastUser as { attachments?: readonly unknown[] }).attachments ?? []),
  ];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getApiBaseUrl(): string {
  const configured = (import.meta as any).env?.VITE_API_BASE_URL as
    | string
    | undefined;
  return configured?.trim()
    ? configured
    : typeof window !== "undefined"
      ? window.location.origin
      : "";
}

function extractLastUserText(
  messages: ChatModelRunOptions["messages"],
): string {
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  if (!lastUser) return "";
  return lastUser.content
    .filter((p) => p.type === "text")
    .map((p) => (p as { type: "text"; text: string }).text)
    .join(" ");
}

function getMockInitialMessages(): ThreadMessageLike[] {
  return [
    {
      role: "assistant",
      content: [
        {
          type: "text",
          text: "## Welcome to INSAION Copilot\n\nI'm your **robotics monitoring and observability assistant**. I can help you:\n\n- 🔍 **Diagnose incidents** — root cause analysis, timeline reconstruction\n- 📊 **Inspect telemetry** — drift detection, sensor fusion analysis\n- 🚨 **Explain alarms** — correlate events, suggest remediation\n- 📦 **Extract MCAP traces** — request raw packet data from edge devices\n- 🤖 **Fleet health** — multi-device status, comparative analysis\n\nAsk me anything about your robot fleet.",
        },
      ],
    },
    {
      role: "user",
      content: [
        {
          type: "text",
          text: "Give me a concise summary of incident INC-2026-0042.",
        },
      ],
    },
    {
      role: "assistant",
      content: [
        {
          type: "reasoning",
          text: "The user is asking about INC-2026-0042. Let me recall the incident details:\n- Started at 14:03:12 UTC on device dev-07\n- Root event: lidar timestamp skew of ~340ms\n- Cascaded into navigation confidence drop\n- Two automated retries were triggered\n- Resolved at 14:06:41 UTC after NTP correction",
        } as any,
        {
          type: "text",
          text: "## Incident INC-2026-0042 Summary\n\n**Status:** ✅ Resolved | **Duration:** 3m 29s | **Affected Device:** `dev-07`\n\n### Timeline\n\n| Time (UTC) | Event |\n|---|---|\n| 14:03:12 | Lidar timestamp skew detected (+340ms) |\n| 14:03:18 | Navigation confidence dropped below 0.72 threshold |\n| 14:03:45 | Auto-retry 1 triggered — partial recovery |\n| 14:04:20 | Network burst on `/tf` delayed control fusion |\n| 14:05:10 | Auto-retry 2 triggered — full recovery |\n| 14:06:41 | System nominal, incident closed |\n\n### Root Cause\n\nNTP drift on `dev-07` caused a **340ms timestamp skew** in the lidar pipeline, which propagated to the sensor fusion layer and degraded navigation confidence below operational threshold.\n\n### Recommended Actions\n\n1. Tighten NTP offset alerting (current threshold: 500ms → recommend 100ms)\n2. Add per-topic lag dashboard for `/tf` and `/lidar/points`\n3. Schedule 10-minute MCAP capture post-incident for forensic analysis",
        },
      ],
    },
  ];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Creates a headless assistant-ui local runtime wired to our FastAPI
 * `/api/v2/copilot/triage-stream` SSE endpoint.
 *
 * The hook reads `activeContext` and the auth token from Redux via refs so
 * that the adapter always uses the latest values without needing to be
 * recreated between renders.
 */
export function useCopilotRuntime(): AssistantRuntime {
  const dispatch = useAppDispatch();
  const activeContext = useAppSelector((s) => s.copilotReducer.activeContext);
  const activeThreadId = useAppSelector((s) => s.copilotReducer.activeThreadId);
  const appToken = useAppSelector((s) => s.appAuthReducer.appToken);
  const isMockMode = (import.meta as any).env?.VITE_COPILOT_MOCK === "true";

  // Mutable refs so the stable adapter closure always sees the latest values.
  const contextRef = useRef(activeContext);
  const threadIdRef = useRef(activeThreadId);
  const tokenRef = useRef(appToken);

  useEffect(() => {
    contextRef.current = activeContext;
  }, [activeContext]);

  useEffect(() => {
    threadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    tokenRef.current = appToken;
  }, [appToken]);

  // Build the adapter once; the closure over refs keeps it up-to-date.
  const adapterRef = useRef<ChatModelAdapter>({
    run: async function* ({
      messages,
      runConfig,
      abortSignal,
    }: ChatModelRunOptions): AsyncGenerator<ChatModelRunResult, void> {
      const promptText = extractLastUserText(messages);
      const sessionId = crypto.randomUUID();
      const { incidentId, deviceId } = contextRef.current;
      const attachments = extractLastUserAttachments(messages);
      // On regenerate (action-bar reload) the triggering user message already
      // exists in the DB — it must not be persisted again, or it duplicates on
      // the next thread hydration.
      const isRegenerate = runConfig?.custom?.regenerate === true;

      dispatch(clearActivity());
      dispatch(
        pushActivity({
          kind: "status",
          text: promptText
            ? `Starting analysis: \"${promptText.slice(0, 120)}${promptText.length > 120 ? "..." : ""}\"`
            : "Starting analysis",
        }),
      );

      if (isMockMode) {
        dispatch(setStatusText("HYDRATING_TELEMETRY"));
        await new Promise((r) => setTimeout(r, 700)); // Initial system lag delay

        const contextPrefix =
          incidentId || deviceId
            ? `Context attached — incident: \`${incidentId ?? "n/a"}\`, device: \`${deviceId ?? "n/a"}\``
            : "";

        const baseReasoning = contextPrefix
          ? `Pulling telemetry context for ${incidentId ?? deviceId}...\n1. Loading incident timeline\n2. Correlating sensor fusion logs\n3. Checking upstream NTP alignment\n4. Cross-referencing alarm events`
          : `Analyzing your query: "${promptText}"\n1. Identifying relevant telemetry signals\n2. Checking recent alarm history\n3. Evaluating fleet health metrics`;

        dispatch(setStatusText("RUNNING_LLM_EVALUATION"));

        // 1. Stream the initial "Reasoning / Chain of Thought" block
        let currentReasoning = "";
        const reasoningStep = 6; // chunk slice size
        for (let i = 0; i < baseReasoning.length; i += reasoningStep) {
          if (abortSignal?.aborted) return;
          currentReasoning = baseReasoning.slice(0, i + reasoningStep);
          yield {
            content: [{ type: "reasoning", text: currentReasoning }] as any[],
          };
          await new Promise((r) => setTimeout(r, 10)); // 10ms typing speed
        }

        await new Promise((r) => setTimeout(r, 400)); // Small cognitive pause
        const shouldSuspend = /mcap|packet|trace|raw|extract/i.test(promptText);

        if (shouldSuspend) {
          const appendedReasoning = `${baseReasoning}\n5. Root-cause confidence: **low** — insufficient raw packet data\n6. Recommending MCAP extraction from edge device`;

          // Flash final reasoning adjustment
          yield {
            content: [{ type: "reasoning", text: appendedReasoning }] as any[],
          };

          const suspendText =
            "I need to collect raw packet timing data before I can complete this analysis.\n\nRoot-cause confidence is **limited** without packet-level traces from the sensor fusion pipeline. I'm requesting an MCAP slice from the edge device — please review and approve the extraction below.";

          // Stream the markdown text while maintaining the static reasoning block
          let currentText = "";
          const textStep = 8;
          for (let i = 0; i < suspendText.length; i += textStep) {
            if (abortSignal?.aborted) return;
            currentText = suspendText.slice(0, i + textStep);
            yield {
              content: [
                { type: "reasoning", text: appendedReasoning },
                { type: "text", text: currentText },
              ] as any[],
            };
            await new Promise((r) => setTimeout(r, 12));
          }

          // Complete the run by injecting the custom suspension data card at the end
          dispatch(setStatusText("SUSPENDED_AWAITING_MCAP_APPROVAL"));
          yield {
            content: [
              { type: "reasoning", text: appendedReasoning },
              { type: "text", text: suspendText },
              {
                type: "data",
                name: "mcap_suspension",
                data: {
                  justification:
                    "Sensor fusion lag causality requires packet-level trace. Timestamp skew of >200ms detected on `/lidar/points` — need raw data to differentiate hardware vs. software root cause.",
                  estimated_file_size_bytes: 17825792,
                  device_id: deviceId ?? "dev-mock-07",
                  incident_id: incidentId ?? "INC-2026-mock",
                  pipelines: ["perception", "control"],
                  time_window_seconds: 180,
                },
              },
            ] as any[],
            status: { type: "complete", reason: "stop" },
          };
          return;
        }

        // Path: Normal Text Content Stream
        const fullReasoning = `${baseReasoning}\n5. Analysis complete — generating structured response`;
        yield {
          content: [{ type: "reasoning", text: fullReasoning }] as any[],
        };

        const finalText = contextPrefix
          ? `${contextPrefix}\n\n## Diagnostic Findings\n\n### 1. Telemetry Analysis\n\nTimestamp alignment shows a **±12ms drift** on \`/lidar/points\` that began approximately 18 minutes before the incident trigger. The drift correlates with an NTP resync event on \`${deviceId ?? "dev-mock-07"}\`.\n\n### 2. Alarm Correlation\n\nThe alarm storm (14 alarms in 40s) was caused by three cascading thresholds:\n\n| Metric | Threshold | Peak Value |\n|--------|-----------|------------|\n| Localization confidence | > 0.80 | 0.61 |\n| \`/tf\` publish rate | < 50 Hz | 31 Hz |\n| Control fusion latency | < 20ms | 78ms |\n\n### 3. Recovery Path\n\nTwo auto-retries successfully restored the perception stack. No actuator faults were detected — the incident was **purely software-side**.\n\n### Recommended Actions\n\n1. **Immediate**: Tighten NTP offset alert threshold from 500ms → 100ms\n2. **Short-term**: Add per-topic publish rate dashboard for \`/tf\` and \`/lidar/points\`\n3. **Preventive**: Schedule bi-weekly MCAP captures on affected devices for forensic baseline`
          : `## Fleet Observability Summary\n\n### Active Status\n\n- **Online devices**: 14 / 16 (2 offline — scheduled maintenance)\n- **Active incidents**: 2 open, 0 critical\n- **Alarm rate** (last 1h): 3 alarms — within normal range\n\n### Recent Highlights\n\n- \`dev-03\` telemetry drift detected: +8ms on \`/imu/data\` (non-critical)\n- \`dev-11\` successfully completed 47km autonomous run — no anomalies\n- Fleet-wide NTP sync health: 13/14 within ±20ms tolerance\n\n### Suggested Focus Areas\n\n1. Review \`dev-03\` IMU calibration schedule\n2. Investigate offline devices (\`dev-05\`, \`dev-09\`) for return-to-service timeline\n3. Check alarm suppression rules after last Tuesday's false-positive wave`;

        let currentText = "";
        const textStep = 14; // Larger chunk step for lengthy markdown reports
        for (let i = 0; i < finalText.length; i += textStep) {
          if (abortSignal?.aborted) return;
          currentText = finalText.slice(0, i + textStep);
          yield {
            content: [
              { type: "reasoning", text: fullReasoning },
              { type: "text", text: currentText },
            ] as any[],
          };
          await new Promise((r) => setTimeout(r, 7));
        }

        // Finalize state
        dispatch(setStatusText("IDLE"));
        yield {
          content: [
            { type: "reasoning", text: fullReasoning },
            { type: "text", text: finalText },
          ] as any[],
          status: { type: "complete", reason: "stop" },
        };
        return;
      }

      dispatch(setStatusText("CONNECTING"));

      const baseUrl = getApiBaseUrl();
      let threadId = threadIdRef.current;
      // Discard any stale settlement left over from a previous suspension in
      // this thread so it cannot instantly resolve a new one.
      if (threadId) clearUploadSettled(threadId);

      try {
        if (promptText && !isRegenerate) {
          if (!threadId) {
            const created = await createCopilotThread({
              first_message: {
                role: "user",
                content_type: "markdown",
                content_payload: { text: promptText },
                attachments,
              },
            });
            threadId = created.thread.id;
            threadIdRef.current = threadId;
            dispatch(setActiveThreadId(threadId));
            dispatch(
              upsertThread({
                id: created.thread.id,
                title: created.thread.title,
                createdAt: created.thread.created_at,
                updatedAt: created.thread.updated_at,
              }),
            );
          } else {
            await createCopilotMessage(threadId, {
              role: "user",
              content_type: "markdown",
              content_payload: { text: promptText },
              attachments,
            });
          }
        }
      } catch (persistErr) {
        dispatch(setStatusText("ERROR"));
        yield {
          content: [
            {
              type: "text" as const,
              text: `Failed to persist message: ${(persistErr as Error).message}`,
            },
          ],
          status: {
            type: "incomplete" as const,
            reason: "error" as const,
            error: String(
              (persistErr as Error).message ?? "Persistence failed",
            ),
          },
        };
        return;
      }

      const body: ChatStreamRequest = {
        session_id: sessionId,
        ...(incidentId ? { incident_id: incidentId } : {}),
        ...(!incidentId && deviceId ? { device_id: deviceId } : {}),
        ...(promptText ? { prompt: promptText } : {}),
        ...(threadId ? { thread_id: threadId } : {}),
        ...(typeof window !== "undefined"
          ? { ui_route: window.location.pathname }
          : {}),
      };

      let response: Response;
      try {
        response = await fetch(`${baseUrl}/v2/copilot/chat-stream`, {
          method: "POST",
          signal: abortSignal,
          headers: {
            "Content-Type": "application/json",
            ...(tokenRef.current
              ? { Authorization: `Bearer ${tokenRef.current}` }
              : {}),
          },
          body: JSON.stringify(body),
        });
      } catch (fetchErr) {
        if ((fetchErr as Error)?.name === "AbortError") return;
        dispatch(setStatusText("ERROR"));
        yield {
          content: [
            {
              type: "text" as const,
              text: `Connection failed: ${(fetchErr as Error).message}`,
            },
          ],
          status: {
            type: "incomplete" as const,
            reason: "error" as const,
            error: String((fetchErr as Error).message ?? "Connection failed"),
          },
        };
        return;
      }

      if (!response.ok || !response.body) {
        dispatch(setStatusText("ERROR"));
        const payload = await response.json().catch(() => undefined);
        const httpError = copilotHttpError(
          response.status,
          response.statusText,
          payload,
        );
        yield {
          content: [
            {
              type: "text" as const,
              text: httpError.text,
            },
          ],
          status: {
            type: "incomplete" as const,
            reason: "error" as const,
            error: httpError.error,
          },
        };
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let suspensionData: unknown | null = null;
      const creditUsage: CopilotCreditUsage = {
        creditsConsumed: 0,
        creditBalance: 0,
      };

      const addCreditUsage = (event: SSEUsageEvent) => {
        creditUsage.creditsConsumed += event.credits_consumed;
        creditUsage.creditBalance = event.credit_balance;
      };

      const usageMetadata = () =>
        creditUsage.creditsConsumed > 0
          ? { custom: { creditUsage: { ...creditUsage } } }
          : undefined;

      type StreamPart =
        | { type: "reasoning"; text: string }
        | { type: "text"; text: string }
        | { type: "data"; name: "mcap_suspension"; data: unknown };

      const timeline: StreamPart[] = [];

      const appendTextPart = (type: "reasoning" | "text", text: string) => {
        const lastPart = timeline.at(-1);
        if (lastPart?.type === type) {
          lastPart.text += text;
        } else {
          timeline.push({ type, text });
        }
      };

      // Build the message content parts from whatever has streamed so far.
      // Clone the append-only timeline so assistant-ui receives a new snapshot
      // while preserving the exact SSE event order.
      const buildParts = (): ChatModelRunResult["content"] & Array<unknown> => {
        return timeline.map((part) => ({
          ...part,
        })) as ChatModelRunResult["content"] & Array<unknown>;
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const rawData = line.slice(6).trim();
            if (!rawData || rawData === "[DONE]") continue;

            let event: SSEEvent;
            try {
              event = JSON.parse(rawData) as SSEEvent;
            } catch {
              continue;
            }

            if (event.type === "status") {
              dispatch(setStatusText(event.status));
              const label = statusLabel(event.status);
              dispatch(
                pushActivity({
                  kind: "status",
                  text: label,
                }),
              );
            } else if (event.type === "reasoning") {
              appendTextPart("reasoning", event.content);
              for (const line of splitReasoningChunks(event.content)) {
                dispatch(
                  pushActivity({
                    kind: reasoningKind(line),
                    text: line,
                  }),
                );
              }
              yield { content: buildParts() };
            } else if (event.type === "text") {
              appendTextPart("text", event.content);
              yield { content: buildParts() };
            } else if (event.type === "suspension") {
              // Surface the MCAP approval card as a data part but KEEP this
              // run (and therefore this assistant message) alive: after the
              // operator approves and the extraction settles, the same turn
              // continues with the live deep-analysis stream.
              suspensionData = {
                ...event.payload,
                thread_id: event.payload.thread_id || threadId,
              };
              timeline.push({
                type: "data",
                name: "mcap_suspension",
                data: suspensionData,
              });
              dispatch(setStatusText("SUSPENDED_AWAITING_MCAP_APPROVAL"));
              yield { content: buildParts() };
            } else if (event.type === "usage") {
              addCreditUsage(event);
              yield { content: buildParts(), metadata: usageMetadata() };
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // ── Same-turn continuation: MCAP upload → live deep analysis ────────
      if (suspensionData && threadId) {
        // Wait for the approval card to report the extraction settled (it
        // tracks the upload jobs live via the recording-status SSE stream).
        const settlement = await waitForUploadSettled(threadId, abortSignal);
        if (settlement === null) return; // run aborted while waiting

        if (!settlement.ok) {
          dispatch(setStatusText("IDLE"));
          yield {
            content: buildParts(),
            status: { type: "complete" as const, reason: "stop" as const },
            metadata: usageMetadata(),
          };
          return;
        }

        dispatch(setStatusText("ANALYZING_MCAP"));
        dispatch(
          pushActivity({
            kind: "status",
            text: "MCAP extraction complete — starting deep analysis",
          }),
        );

        try {
          const analysisResp = await fetch(
            `${baseUrl}/v2/copilot/analysis-stream`,
            {
              method: "POST",
              signal: abortSignal,
              headers: {
                "Content-Type": "application/json",
                ...(tokenRef.current
                  ? { Authorization: `Bearer ${tokenRef.current}` }
                  : {}),
              },
              body: JSON.stringify({ thread_id: threadId }),
            },
          );
          if (!analysisResp.ok || !analysisResp.body) {
            throw new Error(
              `HTTP ${analysisResp.status}: ${analysisResp.statusText}`,
            );
          }

          const analysisReader = analysisResp.body.getReader();
          let analysisBuffer = "";
          try {
            while (true) {
              const { done, value } = await analysisReader.read();
              if (done) break;
              analysisBuffer += decoder.decode(value, { stream: true });
              const lines = analysisBuffer.split("\n");
              analysisBuffer = lines.pop() ?? "";
              for (const line of lines) {
                if (!line.startsWith("data: ")) continue;
                const rawData = line.slice(6).trim();
                if (!rawData || rawData === "[DONE]") continue;
                let event: SSEEvent;
                try {
                  event = JSON.parse(rawData) as SSEEvent;
                } catch {
                  continue;
                }
                if (event.type === "status") {
                  dispatch(setStatusText(event.status));
                  const label = statusLabel(event.status);
                  dispatch(
                    pushActivity({
                      kind: "status",
                      text: label,
                    }),
                  );
                } else if (event.type === "reasoning") {
                  appendTextPart("reasoning", event.content);
                  for (const l of splitReasoningChunks(event.content)) {
                    dispatch(pushActivity({ kind: reasoningKind(l), text: l }));
                  }
                  yield { content: buildParts() };
                } else if (event.type === "text") {
                  appendTextPart("text", event.content);
                  yield { content: buildParts() };
                } else if (event.type === "usage") {
                  addCreditUsage(event);
                  yield { content: buildParts(), metadata: usageMetadata() };
                }
              }
            }
          } finally {
            analysisReader.releaseLock();
          }
        } catch (analysisErr) {
          if ((analysisErr as Error)?.name === "AbortError") return;
          appendTextPart(
            "text",
            `\n\n**Deep analysis stream failed** — ` +
              `${(analysisErr as Error).message}. Reopen this conversation to ` +
              "retrieve the findings once the analysis completes.",
          );
          yield { content: buildParts() };
        }
      }

      dispatch(setStatusText("IDLE"));
      // Final complete yield with the full chronological timeline.
      yield {
        content: buildParts(),
        status: { type: "complete" as const, reason: "stop" as const },
        metadata: usageMetadata(),
      };

      // Maintain the conversation title: summarize the exchange and, if the
      // topic changed (or the title is still the raw first message), regenerate
      // it. Best-effort and non-blocking.
      // if (threadId) {
      //   const titleThreadId = threadId;
      //   void generateCopilotThreadTitle(titleThreadId)
      //     .then((title) => {
      //       if (title) {
      //         dispatch(renameThread({ id: titleThreadId, title }));
      //       }
      //     })
      //     .catch(() => {
      //       /* title refresh is non-critical */
      //     });
      // }
    },
  });

  // Static suggestion adapter — returns contextual follow-up prompts.
  const suggestionAdapterRef = useRef<SuggestionAdapter>({
    async generate() {
      return [
        { prompt: "What are the root causes of the last 5 incidents?" },
        {
          prompt:
            "Show me telemetry drift trends for the fleet over the past 24h",
        },
        { prompt: "Which devices are at risk of a localization failure?" },
        {
          prompt:
            "Generate a postmortem report for the most recent critical incident",
        },
        {
          prompt:
            "Extract MCAP trace from the affected device for forensic analysis",
        },
      ];
    },
  });

  return useLocalRuntime(adapterRef.current, {
    initialMessages: isMockMode ? getMockInitialMessages() : undefined,
    adapters: {
      suggestion: suggestionAdapterRef.current,
    },
  });
}
